import { useLayoutEffect, useRef, useState } from "react";
import { useStoreContext } from "./StoreContext";
import { observeStore } from "../../../utils/store";
import { IGlobalReducer } from "../../../store/reducers";
import { IModuleReducer } from "../../../modules/reducers";

// Simple equality check - can be replaced with a custom comparator
function defaultEqualityCheck<T>(a: T, b: T): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

export function useStoreSelector<T>(
    selector: (state: DeepReadonlyObject<IGlobalReducer & IModuleReducer>) => T,
    equalityFn: (a: T, b: T) => boolean = defaultEqualityCheck
): T {
    const { store } = useStoreContext();
    const selectorRef = useRef(selector);
    const equalityFnRef = useRef(equalityFn);
    const [, forceRender] = useState({});
    const latestSelectedStateRef = useRef<T | undefined>(undefined);
    const latestSubscriptionCallbackErrorRef = useRef<Error | undefined>(undefined);

    // Keep refs up to date
    selectorRef.current = selector;
    equalityFnRef.current = equalityFn;

    // Get current selected state
    let selectedState: T;

    try {
        selectedState = selector(store.getState());
    } catch (err) {
        // If selector throws, use previous value and re-throw after render
        if (latestSubscriptionCallbackErrorRef.current) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            selectedState = latestSelectedStateRef.current!;
        } else {
            throw err;
        }
    }

    // Check if we need to update (use layout effect logic)
    useLayoutEffect(() => {
        latestSubscriptionCallbackErrorRef.current = undefined;
        latestSelectedStateRef.current = selectedState;
    });

    // Subscribe to store changes
    useLayoutEffect(() => {
        let didUnsubscribe = false;

        const checkForUpdates = () => {
            if (didUnsubscribe) {
                return;
            }

            try {
                const newSelectedState = selectorRef.current(store.getState());

                // Skip equality check if this is the first run
                if (latestSelectedStateRef.current !== undefined) {
                    if (equalityFnRef.current(newSelectedState, latestSelectedStateRef.current)) {
                        return;
                    }
                }

                latestSelectedStateRef.current = newSelectedState;
            } catch (err) {
                // Store error for next render
                latestSubscriptionCallbackErrorRef.current = err as Error;
            }

            // Force re-render - defer to avoid updating during another component's render
            queueMicrotask(() => {
                if (!didUnsubscribe) {
                    forceRender({});
                }
            });
        };

        // Use a stable wrapper for observeStore
        const unsubscribe = observeStore(
            store,
            (state) => selectorRef.current(state),
            checkForUpdates
        );

        // Check for updates that happened between render and subscription
        checkForUpdates();

        return () => {
            didUnsubscribe = true;
            unsubscribe();
        };
    }, [store]);

    // Re-throw selector errors after render
    if (latestSubscriptionCallbackErrorRef.current) {
        throw latestSubscriptionCallbackErrorRef.current;
    }

    return selectedState;
}